import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import {
  StageContentIndicatorColor,
  StageContentPosition,
  StageHeight,
  StageLocalization,
  StageSchemeDefinition,
  StageSchemeName
} from "./constants/stage-types";
import {
  STAGE_HEIGHT_AUTO,
  STAGE_HEIGHT_FIXED,
  STAGE_HEIGHT_FULL,
  STAGE_INDICATOR_COLOR_DARK,
  STAGE_INDICATOR_COLOR_LIGHT,
  STAGE_SCHEME_CONFIG,
  STAGE_SCHEME_MAP
} from "./constants/stage-constants";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { isMobile } from "../../utils/screen-helper";
import { getFirstNestedChild, getWebsiteNavigation, preciseSlottedChildSelector } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { StageSchemeConfiguration } from "../../constants/colors";
import {
  IMAGE_COMPONENTS_ON_BRIGHT_IMAGE_BACKGROUND_SCHEME,
  IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME
} from "../../constants/global-constants";
import { scrollToElement } from "../../utils/scroll-helper";
import { STAGE_LOCALIZATION } from "./constants/stage-i18n";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { isTouchDevice } from "../../utils/touch-helper";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot headline - Holds a single eon-ui-headline element. Use headline sizes between h1 and h3. Other sizes are clamped automatically to ensure brand compliance.
 * @slot image - Holds a single eon-ui-image element
 * @slot video - Holds a single eon-ui-videoplayer element
 * @slot ctas - Holds one or two eon-ui-link and/or eon-ui-button elements.
 */
@Component({
  tag: "eon-ui-stage",
  styleUrl: "stage.scss",
  assetsDirs: ["assets-stage"],
  shadow: true
})
export class Stage
  implements
    ColorableComponent<StageSchemeConfiguration<StageSchemeDefinition>>,
    LocalizableComponent<StageLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: StageSchemeConfiguration<StageSchemeDefinition> = STAGE_SCHEME_CONFIG;
  schemeMap = STAGE_SCHEME_MAP;

  localization: Localization<StageLocalization> = STAGE_LOCALIZATION;
  currentLocalization: StageLocalization;

  @State() activeScheme: StageSchemeDefinition;
  @State() backgroundVideo;
  @State() allowContentAlignment: boolean = false;
  @State() heightAdjustment;
  @State() isPlaying: boolean = true;
  @State() indicatorColor: StageContentIndicatorColor = STAGE_INDICATOR_COLOR_LIGHT;
  @State() hasViolator: boolean;
  @State() isMobile = isMobile();

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiStageElement;

  private image: HTMLEonUiImageElement;
  private video: HTMLEonUiVideoplayerElement;
  private violatorSize: string;
  private backgroundElement;
  private backgroundAppearance;

  /**
   * Raised when the component has been loaded.
   */
  @Event() stageLoad: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: StageSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Defines where the content is shown inside the stage
   * @example <eon-ui-stage content-position="center"></eon-ui-stage>
   */
  @Prop() contentPosition: StageContentPosition;

  /**
   * Defines the text inside the sub headline
   * @example <eon-ui-stage subheadline-text="this is a subheadline"></eon-ui-stage>
   */
  @Prop({ mutable: true }) subheadlineText: string;

  /**
   * Defines the height of the header of the page where the stage is used. The height of the stage is calculated depending onthis height since
   * both the header and the stage together should take up to 80% of the hight of the viewport. The client should find that value dynamically
   * and pass it down.
   * @example <eon-ui-stage header-height="148"></eon-ui-stage>
   */
  @Prop() headerHeight: number;

  /**
   * Defines the stage to be scaled across the viewport.
   * @example <eon-ui-stage height="fixed"></eon-ui-stage>
   */
  @Prop() height: StageHeight = STAGE_HEIGHT_FIXED;

  /**
   * Will remove the background color of the content box as long as it is placed on the background.
   */
  @Prop() transparentBoxBackground: boolean = false;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Emitted as soon as the innerBackground property changes.
   */
  @Event({ bubbles: false }) innerBackgroundUpdate: EventEmitter;

  componentWillLoad() {
    this.deprecatedFallback();
    this.updateIsMobileState();
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");
    this.image = preciseSlottedChildSelector(this.hostElement, "image", "EON-UI-IMAGE") as HTMLEonUiImageElement;
    this.video = preciseSlottedChildSelector(
      this.hostElement,
      "video",
      "EON-UI-VIDEOPLAYER"
    ) as HTMLEonUiVideoplayerElement;

    if (this.image) this.image.useAsBackground = true;
    if (this.video) this.video.useAsBackground = true;
    if (this.hasViolator) {
      this.violatorSize = (getFirstNestedChild(this.hostElement, "eon-ui-violator") as HTMLElement).getAttribute(
        "size"
      );
    }

    this.applyScheme();
    this.setCurrentLocalization();

    this.video = getFirstNestedChild(this.hostElement, "eon-ui-videoplayer");
    if (this.video) {
      this.setVideoProperties();
    }

    this.video && this.enterViewport(this.hostElement);

    this.updateProcessedText();
    this.height && this.handleHeightCalculation();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.stageLoad.emit();
  }

  deprecatedFallback() {
    this.height =
      this.hostElement.hasAttribute("full-height") && this.hostElement.getAttribute("full-height") !== "false"
        ? STAGE_HEIGHT_FULL
        : this.height;
  }

  /**
   * This function is calculating the Stage height
   * On basis of whether Navigation or ThirdlevelNavigation is present
   * And then summation value is assigned to "header-height" property for final Stage height calculation.
   */
  @Listen("resize", { target: "window" })
  @Watch("height")
  async handleHeightCalculation() {
    if (!this.headerHeight && this.height === STAGE_HEIGHT_FULL) {
      const navigationHeight = await getWebsiteNavigation()?.updateNavigationHeight();
      const thirdLevelNavigationHeight = await document
        .getElementsByTagName("eon-ui-third-level-navigation")[0]
        ?.updateHeight();
      const bottomDrawerHeight = await document.getElementsByTagName("eon-ui-bottom-drawer")[0]?.getHeight();

      this.heightAdjustment =
        (navigationHeight ? navigationHeight : 0) +
        (thirdLevelNavigationHeight ? thirdLevelNavigationHeight : 0) +
        (bottomDrawerHeight ? bottomDrawerHeight : 0);

      if (Number.isInteger(this.heightAdjustment)) {
        this.hostElement.style.setProperty("--header-height", `${this.heightAdjustment}px`);
      } else {
        this.hostElement.style.setProperty("--header-height", "0px");
      }
    }
  }

  @Watch("headerHeight")
  updateHeaderHeightCSSVariable() {
    if (Number.isInteger(this.headerHeight)) {
      this.hostElement.style.setProperty("--header-height", `${this.headerHeight}px`);
    } else {
      this.hostElement.style.setProperty("--header-height", "0px");
    }
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);

    if (this.image) this.backgroundElement = this.image;
    if (this.video) this.backgroundElement = this.video;

    if (this.backgroundElement) {
      switch (this.backgroundElement.appearance) {
        case "bright":
          this.backgroundAppearance = IMAGE_COMPONENTS_ON_BRIGHT_IMAGE_BACKGROUND_SCHEME;
          this.indicatorColor = STAGE_INDICATOR_COLOR_DARK;
          break;
        case "dark":
          this.backgroundAppearance = IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME;
          this.indicatorColor = STAGE_INDICATOR_COLOR_LIGHT;
          break;
        default:
          this.backgroundAppearance = IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME;
          this.indicatorColor = STAGE_INDICATOR_COLOR_LIGHT;
          break;
      }
    }

    this.setInnerBackgroundAndUpdateChildSchemes();
  }

  @Watch("isMobile")
  @Watch("transparentBoxBackground")
  setInnerBackgroundAndUpdateChildSchemes() {
    if (this.backgroundElement && this.transparentBoxBackground && !isMobile()) {
      this.innerBackground = this.backgroundAppearance;
    } else {
      this.innerBackground = this.activeScheme.backgroundColor;
    }

    this.innerBackgroundUpdate.emit(this.innerBackground);
  }

  @Listen("resize", { target: "window" })
  updateIsMobileState() {
    this.isMobile = isMobile();
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("subheadlineText")
  updateProcessedText(): void {
    const slashN = "\\n";
    this.subheadlineText = this.subheadlineText && this.subheadlineText.split(slashN).join("<br />");
  }

  handleIndicatorClick() {
    scrollToElement(this.hostElement, {
      targetPosition: "end",
      offset:
        this.hostElement.offsetHeight +
        (Number.isInteger(this.heightAdjustment)
          ? this.heightAdjustment
          : window.innerHeight - this.hostElement.offsetHeight)
    });
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  private setVideoProperties() {
    this.backgroundVideo = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.tagName === "EON-UI-VIDEOPLAYER";
      }
    );
    this.backgroundVideo.forEach((backgroundVideo) => {
      backgroundVideo.setAttribute("use-as-background", "true");
    });
  }

  handlePlayToggle() {
    this.isPlaying ? this.video.pausePlayback() : this.video.startPlayback();
    this.isPlaying = !this.isPlaying;
  }

  private enterViewport(element: HTMLElement) {
    if (window && window.IntersectionObserver) {
      const videoObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.isPlaying = true;
            } else {
              this.isPlaying = false;
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      videoObserver.observe(element);
    }
  }

  private mapContentPosition() {
    if ((this.contentPosition as string) === "center") {
      return "bottom";
    } else {
      return this.contentPosition || "bottom";
    }
  }

  render() {
    const showVideoPlayBackControls = this.video || (this.video && (isMobile() || isTouchDevice()));

    return (
      <Host>
        <div
          class={pfx({
            stage: true,
            [this.mapContentPosition()]: true,
            "full-height": this.height === STAGE_HEIGHT_FULL,
            "auto-height": this.height === STAGE_HEIGHT_AUTO,
            "has-violator": !!this.hasViolator,
            [`violator-${this.violatorSize}`]: !!this.violatorSize
          })}
        >
          <div class={pfx("stage-media")}>
            <slot name="video"></slot>
            <slot name="image"></slot>
          </div>

          {showVideoPlayBackControls && (
            <div
              class={pfx({
                ["video-control-wrapper"]: true
              })}
            >
              <eon-ui-button
                text={this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause}
                icon={this.isPlaying ? "pause" : "play"}
                iconPosition="left"
                size="small"
                onButtonClick={() => this.handlePlayToggle()}
              ></eon-ui-button>
            </div>
          )}
          <div
            class={pfx({
              ["content-wrapper"]: true,
              [this.mapContentPosition()]: true
            })}
          >
            {this.hasViolator && (
              <div
                class={pfx({
                  "stage-violator": true
                })}
              >
                <slot name="violator"></slot>
              </div>
            )}
            <div
              class={pfx({
                ["stage-content"]: true,
                ["transparent-background"]: this.transparentBoxBackground,
                gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
              })}
            >
              <div class={pfx("stage-headline")}>
                <slot name="headline"></slot>
              </div>

              {this.subheadlineText && (
                <eon-ui-text
                  internalTag={"p"}
                  textStyle={"abstract"}
                  class={pfx({
                    ["subheadline"]: true
                  })}
                >
                  {this.subheadlineText}
                </eon-ui-text>
              )}
              <div class={pfx("stage-ctas")}>
                <slot name="cta"></slot>
              </div>
            </div>
          </div>
          {this.height === STAGE_HEIGHT_FULL && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              class={pfx({
                ["full-height-indicator-wrapper"]: true
              })}
              role="button"
              tabIndex={0}
              onClick={() => this.handleIndicatorClick()}
            >
              <svg
                class={pfx({
                  ["indicator-svg"]: true,
                  [`indicator-${this.indicatorColor}`]: this.indicatorColor
                })}
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 49 48"
              >
                <path
                  class={pfx({
                    ["indicator-path"]: true
                  })}
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M3.10826 6.55155C0.962402 9.96667 0.962402 14.6444 0.962402 24C0.962402 33.3556 0.962402 38.0333 3.10826 41.4484C4.22725 43.2293 5.7331 44.7352 7.51395 45.8541C10.9291 48 15.6068 48 24.9624 48C34.318 48 38.9957 48 42.4109 45.8541C44.1917 44.7352 45.6976 43.2293 46.8165 41.4484C48.9624 38.0333 48.9624 33.3556 48.9624 24C48.9624 14.6444 48.9624 9.96667 46.8165 6.55155C45.6976 4.7707 44.1917 3.26485 42.4109 2.14586C38.9957 0 34.318 0 24.9624 0C15.6068 0 10.9291 0 7.51395 2.14586C5.7331 3.26485 4.22725 4.7707 3.10826 6.55155ZM30.1695 25.7071C30.56 25.3166 30.56 24.6834 30.1695 24.2929C29.779 23.9024 29.1458 23.9024 28.7553 24.2929L25.9624 27.0858V18.5C25.9624 17.9477 25.5147 17.5 24.9624 17.5C24.4101 17.5 23.9624 17.9477 23.9624 18.5V27.0858L21.1695 24.2929C20.779 23.9024 20.1458 23.9024 19.7553 24.2929C19.3648 24.6834 19.3648 25.3166 19.7553 25.7071L24.2553 30.2071C24.6458 30.5976 25.279 30.5976 25.6695 30.2071L30.1695 25.7071Z"
                />
              </svg>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
